import { getLocale, getCurrentShippingOption } from '@ynap/cart-state'
import getCart from './getCart'
import getShippingAddressStep, { getCurrentShippingAddress, getShippingAddressesByCountry } from './getShippingAddressStep'
import getShippingOptions from './getShippingOptionsStep'
import getPackagingOptions from './getPackagingOptionsStep'
import getPaymentOptionsStep, { getPaymentOptions, isCvvRequired, getCardTypeInfo } from './getPaymentStep'
import { getAnalyticsConfig } from '@ynap/cart-state/src/selectors/getConfig'

import { createCombinedSelector } from '../utils/selectorUtils'
import { Messages, ShoppingCart } from 'app/types/common-types'

import { Countries } from '@ynap/address-form-v1'
import { Config, Locale, StoreState } from 'app/state/types'

export default createCombinedSelector({
    name: 'getOrderReviewStep',
    state: ['locale', 'config', 'cart', 'deviceType', 'countries', 'isShippingAddressValid', 'sidebar'],
    props: ['messages'],
    passThroughState: ['isCheckingOut', 'checkoutError', 'checkoutNotification'],
    fn: getOrderReviewStepInternal,
})

interface State extends StoreState {
    cart: ShoppingCart
    config: Config
    countries: Countries
    locale: Locale
    sidebar: boolean
}

type Props = {
    messages: Messages
}

export function getOrderReviewStepInternal(state: State, props: Props) {
    const { locale = {}, config, sidebar } = state
    const {
        lineItems,
        numItems,
        totals,
        genericTotal,
        DDPCountry,
        DDPIncCountry,
        deliveryDuty,
        dduAcceptance,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics,
        taxAndDuty,
        localCustomsFee,
        storeCredit,
        taxAndDutyFlag,
        getApplePayRequest,
        hasJITItems,
        remoteItems,
        localItems,
        isPostSplit,
    } = getCart(state, props)

    const deviceType = state.deviceType || ''
    const paymentOptions = getPaymentOptions(state)
    const cvvRequired = isCvvRequired(state)
    const cardTypeInfo = getCardTypeInfo(state)
    const { first_name, last_name } = state?.cart?.paymentInstruction?.[0]?.creditCardDetails?.card_holder || {}
    const { isShippingAddressValid } = state
    const currentShippingAddress = getCurrentShippingAddress(state)
    const { country } = getLocale(state)
    const addresses = getShippingAddressesByCountry(state, country)

    // As a temporary measure, we will continue to display DDU Notif in non v3 order review step
    const { showUsTaxUi = false } = config?.head?.flags
    const shouldAskDDU = deliveryDuty === 'DDU' && dduAcceptance !== 'true'
    const showTaxAbove = showUsTaxUi && country === 'US'
    const [currentShippingOption] = getCurrentShippingOption(state, props)

    return {
        config,
        locale,
        lineItems,
        numItems,
        totals,
        isOrderReviewStep: isOrderReadyCheck(state, props),
        analytics: getAnalyticsConfig(state),
        genericTotal,
        DDPCountry,
        DDPIncCountry,
        deliveryDuty,
        dduAcceptance,
        shouldAskDDU,
        paymentOptions,
        cvvRequired,
        cardTypeInfo,
        orderDetailsDefaultClosed: config?.head?.flags?.orderDetailsClosed,
        first_name,
        last_name,
        currentShippingOption,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics: {
            ...checkoutAnalytics,
            shippingAddress: currentShippingAddress,
            shippingOption: currentShippingOption,
        },
        taxAndDuty,
        localCustomsFee,
        storeCredit,
        taxAndDutyFlag,
        currentShippingAddress,
        addresses,
        isShippingAddressValid,
        sidebar,
        getApplePayRequest,
        showTaxAbove,
        deviceType,
        hasJITItems,
        remoteItems,
        localItems,
        isPostSplit,
    }
}

export function isOrderReadyCheck(state: State, props: Props) {
    const { cart } = state
    const { completed: isShippingAddressStepCompleted = false } = getShippingAddressStep(state, props) || {}
    const { completed: isShippingOptionsCompleted = false } = getShippingOptions(state, props) || {}
    const { completed: isPackagingOptionsCompleted = false } = getPackagingOptions(state, props) || {}
    const { completed: isPaymentOptionsCompleted = false } = getPaymentOptionsStep(state, props) || {}

    const allStepsCompleted = isShippingAddressStepCompleted && isShippingOptionsCompleted && isPackagingOptionsCompleted && isPaymentOptionsCompleted
    return allStepsCompleted && !!cart.x_readyToOrder
}
